import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { NAV } from "@/constants/testIds";
import api from "@/lib/api";
import { toast } from "sonner";
import AgeGateDialog from "@/components/AgeGateDialog";
import TierPickerDialog from "@/components/TierPickerDialog";
import ProfileMenu from "@/components/ProfileMenu";

const linkCls = ({ isActive }) =>
  `text-xs md:text-sm font-mono uppercase tracking-wider px-2 py-1 rounded transition-colors ${
    isActive ? "text-yellow-400 bg-[#1a1d28]" : "text-slate-400 hover:text-slate-100"
  }`;

export default function Navbar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ageOpen, setAgeOpen] = useState(false);
  const [tierOpen, setTierOpen] = useState(false);

  const launchCheckout = async (tier = "base") => {
    const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, tier });
    window.location.href = data.checkout_url;
  };

  const subscribe = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (!user.age_verified) {
      setAgeOpen(true);
      return;
    }
    setTierOpen(true);
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0b0c10]/80 border-b border-[#1a1d28]">
      <AgeGateDialog
        open={ageOpen}
        onOpenChange={setAgeOpen}
        onVerified={() => setTierOpen(true)}
      />
      <TierPickerDialog
        open={tierOpen}
        onOpenChange={setTierOpen}
        onPicked={async (tier) => {
          try {
            await launchCheckout(tier);
          } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail === "age_verification_required") {
              setTierOpen(false);
              setAgeOpen(true);
              return;
            }
            toast.error(detail || "Checkout failed");
            throw e;
          }
        }}
      />
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 px-4 md:px-8 py-3">
        <Link to="/feed" className="flex items-center gap-2">
          <span className="telesis-eyebrow">Telesis</span>
          <span className="hidden md:inline text-xs text-slate-500 font-mono">· CAI</span>
        </Link>
        <div className="flex items-center gap-2">
          {user ? (
            <>
              {user.is_member ? (
                <>
                  <Badge
                    data-testid={NAV.memberBadge}
                    className={`font-mono text-[10px] tracking-widest uppercase ${
                      user.membership_tier === "unlimited"
                        ? "bg-yellow-400/15 text-yellow-300 border-yellow-500/40"
                        : "bg-emerald-500/15 text-emerald-300 border-emerald-700/40"
                    }`}
                  >
                    {user.membership_tier === "unlimited" ? "Unlimited" : "Base"}
                  </Badge>
                  {user.membership_tier !== "unlimited" && user.votes_remaining_this_month !== null && (
                    <span
                      data-testid="votes-remaining-badge"
                      className="hidden md:inline text-[10px] font-mono text-slate-400"
                    >
                      {user.votes_remaining_this_month} votes left
                    </span>
                  )}
                </>
              ) : (
                <Button
                  data-testid={NAV.subscribeBtn}
                  onClick={subscribe}
                  size="sm"
                  className="rounded-full bg-yellow-400 text-black hover:bg-yellow-300 font-semibold text-xs"
                >
                  Join · from 20.21 CAD
                </Button>
              )}
              <ProfileMenu onPickMembership={subscribe} />
            </>
          ) : (
            <Link to="/login">
              <Button size="sm" className="rounded-full bg-yellow-400 text-black hover:bg-yellow-300 font-semibold text-xs">
                Sign in
              </Button>
            </Link>
          )}
        </div>
      </div>
      <nav
        data-testid="nav-sublevel"
        className="border-t border-[#1a1d28] bg-[#0b0c10]/60"
      >
        <div className="max-w-7xl mx-auto flex items-center gap-1 md:gap-2 overflow-x-auto px-4 md:px-8 py-2">
          <NavLink to="/feed" data-testid={NAV.linkFeed} className={linkCls}>Feed</NavLink>
          <NavLink to="/create" data-testid={NAV.linkCreate} className={linkCls}>Post</NavLink>
          <NavLink to="/dashboard" data-testid={NAV.linkDashboard} className={linkCls}>CAI</NavLink>
          <NavLink to="/pain" data-testid={NAV.linkPain} className={linkCls}>Pain pool</NavLink>
          <NavLink to="/alt-financier" data-testid={NAV.linkAlt} className={linkCls}>Alt pool</NavLink>
          <NavLink to="/wallet" data-testid={NAV.linkWallet} className={linkCls}>Wallet</NavLink>
          <NavLink to="/playground" data-testid={NAV.linkPlayground} className={linkCls}>Playground</NavLink>
        </div>
      </nav>
    </header>
  );
}
